import React, { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import MenuItem from '@mui/material/MenuItem'
import { KeyType, isKeyType } from '@cbgms/base/utils/keyevent'
import { Box, IconButton, ListItemIcon, ListItemText, Menu, Typography, buttonBaseClasses, svgIconClasses } from '@mui/material'
import { SxProps, Theme } from '@mui/system'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import { capitalizeFirstCharacter } from '@cbgms/base/utils/helpers/capitalizeFirstCharacter'
import { ESearchFilterValue, IMenuItem } from '../../topSearchUtils'
import { useTopSearchContext } from '../../context/TopSearchContext'

const boxStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 1.5,
  borderRadius: (theme: Theme) => `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
  backgroundColor: (theme: Theme) => theme.palette.primaryThemeColor.extraDark,
  color: (theme: Theme) => theme.palette.primary.contrastText,
  width: '180px',
  [`& .${buttonBaseClasses.root}`]: {
    [`& .${svgIconClasses.root}`]: { fill: (theme: Theme) => theme.palette.primary.contrastText },
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
  },
  '&:hover': {
    cursor: 'pointer',
    [`& .${buttonBaseClasses.root}`]: {
      backgroundColor: 'rgba(255,255,255,0.2)'
    }
  }
}

const typographyStyle: SxProps = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  lineHeight: 1
}
interface Props {
  onChange?: (selectedFilter: ESearchFilterValue) => void
}

const _SearchDropdownV2: React.FC<Props> = ({ onChange }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { t } = useTranslation()
  const { setSearchFilter, searchFilter, menuOptions } = useTopSearchContext()
  const selectedMenuItem = useMemo(() => menuOptions.find(a => a.value === searchFilter) || menuOptions?.[0], [menuOptions, searchFilter])
  const handleChange = useCallback(
    (value: ESearchFilterValue) => {
      onChange && onChange(value as ESearchFilterValue)
      setAnchorEl(null)
      setSearchFilter(value as ESearchFilterValue)
    },
    [onChange, setSearchFilter]
  )

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const stopBubbling = useCallback((event: React.KeyboardEvent) => {
    if (isKeyType(event, KeyType.ArrowDownKey) || isKeyType(event, KeyType.ArrowUpKey)) {
      event.stopPropagation()
    }
  }, [])

  return (
    <>
      <Box onClick={handleClickListItem} sx={boxStyle} data-testid='main-search-dropdown'>
        <Typography variant='body1' sx={typographyStyle}>
          {capitalizeFirstCharacter(t(selectedMenuItem.translateKey))}
        </Typography>
        <IconButton size='small' sx={{ ml: 1 }}>
          {!open ? <KeyboardArrowDown fontSize='small' /> : <KeyboardArrowUp fontSize='small' />}
        </IconButton>
      </Box>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} MenuListProps={{ onKeyDown: stopBubbling }}>
        {menuOptions.map((category: IMenuItem) => (
          <MenuItem
            data-testid={`main-search-dropdown-option_${category.value}`}
            key={category.value}
            selected={category.value === searchFilter}
            onClick={() => handleChange(category.value)}
          >
            {category.icon && <ListItemIcon>{category.icon}</ListItemIcon>}
            <ListItemText>{capitalizeFirstCharacter(t(category.translateKey))}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
const SearchDropdownV2 = memo(_SearchDropdownV2)
export default SearchDropdownV2
